export const DAY_KEYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

export const DAY_LABELS = {
  monday: 'Monday',
  tuesday: 'Tuesday',
  wednesday: 'Wednesday',
  thursday: 'Thursday',
  friday: 'Friday',
  saturday: 'Saturday',
  sunday: 'Sunday',
};

const DEFAULT_DAY_SCHEDULE = Object.freeze({
  enabled: false,
  on: '09:00',
  off: '17:00',
});

export function createEmptyWeeklySchedule() {
  return DAY_KEYS.reduce((acc, key) => {
    acc[key] = { ...DEFAULT_DAY_SCHEDULE };
    return acc;
  }, {});
}

export function normalizeTimeString(raw) {
  const match = String(raw || '')
    .trim()
    .match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return null;
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  if (!Number.isInteger(hour) || !Number.isInteger(minute)) return null;
  if (hour < 0 || hour > 23 || minute < 0 || minute > 59) return null;
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
}

function normalizeDaySchedule(input, fallback = DEFAULT_DAY_SCHEDULE) {
  const source = input && typeof input === 'object' ? input : {};
  return {
    enabled: source.enabled != null ? !!source.enabled : !!fallback.enabled,
    on: normalizeTimeString(source.on) || fallback.on || DEFAULT_DAY_SCHEDULE.on,
    off: normalizeTimeString(source.off) || fallback.off || DEFAULT_DAY_SCHEDULE.off,
  };
}

export function normalizeWeeklyPowerSchedule(input, options = {}) {
  const fallback = options.baseSchedule || createEmptyWeeklySchedule();
  const parsed = typeof input === 'string' ? safeParseJson(input, {}) : (input || {});
  const everyDaySource = parsed.every_day || parsed.everyDay || parsed.default || null;
  const everyDayFallback = normalizeDaySchedule(everyDaySource, fallback[DAY_KEYS[0]] || DEFAULT_DAY_SCHEDULE);

  return DAY_KEYS.reduce((acc, key) => {
    const source = parsed[key] || parsed[DAY_LABELS[key]] || everyDaySource || null;
    acc[key] = normalizeDaySchedule(source, fallback[key] || everyDayFallback);
    return acc;
  }, {});
}

export function detectPowerScheduleMode(schedule) {
  if (!schedule) return 'every_day';
  const first = schedule[DAY_KEYS[0]];
  if (!first) return 'every_day';
  const everyDay = DAY_KEYS.every((dayKey) => {
    const day = schedule[dayKey];
    return (
      !!day &&
      !!first &&
      !!day.enabled === !!first.enabled &&
      String(day.on || '') === String(first.on || '') &&
      String(day.off || '') === String(first.off || '')
    );
  });
  return everyDay ? 'every_day' : 'per_day';
}

export function safeParseJson(value, fallback = null) {
  if (value == null) return fallback;
  if (typeof value === 'object') return value;
  if (typeof value !== 'string') return fallback;
  try {
    return JSON.parse(value);
  } catch (err) {
    return fallback;
  }
}

function normalizeOpeningHoursLine(line) {
  return String(line || '')
    .replace(/[\u2009\u202f\u00a0]/g, ' ')
    .replace(/[–—−]/g, '-')
    .replace(/\s+/g, ' ')
    .trim();
}

function parseClockLabel(label) {
  const normalized = normalizeOpeningHoursLine(label);
  if (/24\s*hours/i.test(normalized)) return '00:00';
  const match = normalized.match(/^(\d{1,2})(?::(\d{2}))?\s*(AM|PM)$/i);
  if (!match) return null;
  let hour = Number(match[1]);
  const minute = Number(match[2] || '0');
  const suffix = match[3].toUpperCase();
  if (suffix === 'AM') {
    if (hour === 12) hour = 0;
  } else if (hour !== 12) {
    hour += 12;
  }
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
}

function parseOpeningHoursLine(line) {
  const normalized = normalizeOpeningHoursLine(line);
  const match = normalized.match(/^([A-Za-z]+):\s*(.+)$/);
  if (!match) return null;
  const dayLabel = match[1].toLowerCase();
  if (!DAY_KEYS.includes(dayLabel)) return null;
  const value = match[2];

  if (/closed/i.test(value)) {
    return { dayKey: dayLabel, enabled: false, on: '09:00', off: '17:00' };
  }

  if (/open\s*24\s*hours/i.test(value)) {
    return { dayKey: dayLabel, enabled: true, on: '00:00', off: '23:59' };
  }

  const [openLabel, closeLabel] = value.split(/\s*-\s*/);
  const on = parseClockLabel(openLabel);
  const off = parseClockLabel(closeLabel);
  if (!on || !off) return null;
  return { dayKey: dayLabel, enabled: true, on, off };
}

export function derivePowerScheduleFromSite(site) {
  const schedule = createEmptyWeeklySchedule();
  const openingHoursJson = safeParseJson(site?.opening_hours_json, site?.opening_hours_json);
  const lines = Array.isArray(openingHoursJson?.weekdayDescriptions)
    ? openingHoursJson.weekdayDescriptions
    : [];

  lines.forEach((line) => {
    const parsed = parseOpeningHoursLine(line);
    if (!parsed) return;
    schedule[parsed.dayKey] = {
      enabled: parsed.enabled,
      on: parsed.on,
      off: parsed.off,
    };
  });

  return schedule;
}

function getFormatter(timeZone) {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone,
    weekday: 'long',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  });
}

function getTimeZoneParts(date, timeZone) {
  const parts = getFormatter(timeZone).formatToParts(date);
  const mapped = {};
  parts.forEach((part) => {
    if (part.type !== 'literal') {
      mapped[part.type] = part.value;
    }
  });
  const weekday = String(mapped.weekday || '').toLowerCase();
  return {
    year: Number(mapped.year),
    month: Number(mapped.month),
    day: Number(mapped.day),
    hour: Number(mapped.hour),
    minute: Number(mapped.minute),
    second: Number(mapped.second),
    weekday: DAY_KEYS.includes(weekday) ? weekday : null,
  };
}

function getTimeZoneOffsetMs(date, timeZone) {
  const parts = getTimeZoneParts(date, timeZone);
  const asUtc = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second || 0);
  return asUtc - date.getTime();
}

function localDateFromDate(date, timeZone) {
  const parts = getTimeZoneParts(date, timeZone);
  return {
    year: parts.year,
    month: parts.month,
    day: parts.day,
  };
}

function addLocalDays(localDate, amount) {
  const base = new Date(Date.UTC(localDate.year, localDate.month - 1, localDate.day));
  base.setUTCDate(base.getUTCDate() + amount);
  return {
    year: base.getUTCFullYear(),
    month: base.getUTCMonth() + 1,
    day: base.getUTCDate(),
  };
}

function compareLocalDates(a, b) {
  if (a.year !== b.year) return a.year - b.year;
  if (a.month !== b.month) return a.month - b.month;
  return a.day - b.day;
}

function localDateWeekday(localDate) {
  const weekdayIndex = new Date(Date.UTC(localDate.year, localDate.month - 1, localDate.day)).getUTCDay();
  return DAY_KEYS[(weekdayIndex + 6) % 7];
}

function timeStringToMinutes(timeValue) {
  const normalized = normalizeTimeString(timeValue) || '00:00';
  const [hour, minute] = normalized.split(':').map(Number);
  return hour * 60 + minute;
}

function zonedTimeToUtc(localDate, timeValue, timeZone) {
  const normalized = normalizeTimeString(timeValue) || '00:00';
  const [hour, minute] = normalized.split(':').map(Number);
  const initialUtcMs = Date.UTC(localDate.year, localDate.month - 1, localDate.day, hour, minute, 0);
  let utcDate = new Date(initialUtcMs);
  let offset = getTimeZoneOffsetMs(utcDate, timeZone);
  utcDate = new Date(initialUtcMs - offset);
  offset = getTimeZoneOffsetMs(utcDate, timeZone);
  return new Date(initialUtcMs - offset);
}

function normalizeSegments(segments = []) {
  return [...segments]
    .filter((segment) => segment.start instanceof Date && segment.end instanceof Date && segment.start < segment.end)
    .sort((a, b) => a.start.getTime() - b.start.getTime())
    .reduce((acc, segment) => {
      if (!acc.length) {
        acc.push(segment);
        return acc;
      }
      const prev = acc[acc.length - 1];
      if (segment.start.getTime() <= prev.end.getTime()) {
        prev.end = new Date(Math.max(prev.end.getTime(), segment.end.getTime()));
        return acc;
      }
      acc.push(segment);
      return acc;
    }, []);
}

export function buildAllowedSegmentsForRange({ schedule, enabled, timeZone = 'UTC', rangeStart, rangeEnd }) {
  if (!enabled) return [];
  const start = rangeStart instanceof Date ? rangeStart : new Date(rangeStart);
  const end = rangeEnd instanceof Date ? rangeEnd : new Date(rangeEnd);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || start >= end) return [];

  const normalizedSchedule = normalizeWeeklyPowerSchedule(schedule);
  const localStart = addLocalDays(localDateFromDate(start, timeZone), -1);
  const localEnd = addLocalDays(localDateFromDate(end, timeZone), 1);
  const segments = [];

  for (let current = localStart; compareLocalDates(current, localEnd) <= 0; current = addLocalDays(current, 1)) {
    const weekday = localDateWeekday(current);
    const daySchedule = normalizedSchedule[weekday];
    if (!daySchedule?.enabled) continue;
    const segmentStart = zonedTimeToUtc(current, daySchedule.on, timeZone);
    const wraps = timeStringToMinutes(daySchedule.off) <= timeStringToMinutes(daySchedule.on);
    const segmentEnd = zonedTimeToUtc(wraps ? addLocalDays(current, 1) : current, daySchedule.off, timeZone);
    if (segmentEnd <= start || segmentStart >= end) continue;
    segments.push({
      start: segmentStart < start ? new Date(start) : segmentStart,
      end: segmentEnd > end ? new Date(end) : segmentEnd,
    });
  }

  return normalizeSegments(segments);
}

export function buildClosedSegmentsForRange({ schedule, enabled, timeZone = 'UTC', rangeStart, rangeEnd }) {
  if (!enabled) return [];
  const start = rangeStart instanceof Date ? rangeStart : new Date(rangeStart);
  const end = rangeEnd instanceof Date ? rangeEnd : new Date(rangeEnd);
  const allowed = buildAllowedSegmentsForRange({ schedule, enabled, timeZone, rangeStart: start, rangeEnd: end });
  const closed = [];
  let cursor = start.getTime();

  allowed.forEach((segment) => {
    if (segment.start.getTime() > cursor) {
      closed.push({ start: new Date(cursor), end: segment.start });
    }
    cursor = Math.max(cursor, segment.end.getTime());
  });

  if (cursor < end.getTime()) {
    closed.push({ start: new Date(cursor), end: end });
  }

  return closed;
}

export function buildPowerMarkersForRange({ schedule, enabled, timeZone = 'UTC', rangeStart, rangeEnd }) {
  if (!enabled) return [];
  const start = rangeStart instanceof Date ? rangeStart : new Date(rangeStart);
  const end = rangeEnd instanceof Date ? rangeEnd : new Date(rangeEnd);
  const allowed = buildAllowedSegmentsForRange({ schedule, enabled, timeZone, rangeStart: start, rangeEnd: end });
  const markers = [];

  allowed.forEach((segment) => {
    if (segment.start >= start && segment.start < end) {
      markers.push({ at: segment.start, type: 'on' });
    }
    if (segment.end > start && segment.end <= end) {
      markers.push({ at: segment.end, type: 'off' });
    }
  });

  return markers;
}

export function isRangeAllowedByPowerSchedule({ schedule, enabled, timeZone = 'UTC', start, end }) {
  if (!enabled) return true;
  const normalizedStart = start instanceof Date ? start : new Date(start);
  const normalizedEnd = end instanceof Date ? end : new Date(end);
  if (Number.isNaN(normalizedStart.getTime()) || Number.isNaN(normalizedEnd.getTime()) || normalizedStart >= normalizedEnd) {
    return false;
  }

  const allowed = buildAllowedSegmentsForRange({
    schedule,
    enabled,
    timeZone,
    rangeStart: normalizedStart,
    rangeEnd: normalizedEnd,
  });

  let cursor = normalizedStart.getTime();
  for (const segment of allowed) {
    if (segment.end.getTime() <= cursor) continue;
    if (segment.start.getTime() > cursor) return false;
    cursor = Math.max(cursor, segment.end.getTime());
    if (cursor >= normalizedEnd.getTime()) return true;
  }
  return cursor >= normalizedEnd.getTime();
}
